"use client";

import Link from "next/link";
import { BlogSearchForm } from "./blog-search-form";
import { BlogPostCard } from "./blog-post-card";
import { BlogPagination } from "./blog-pagination";
import { BLOG_PAGE_SIZE } from "@/lib/blog/constants";
import { useBlogPosts } from "@/hooks/use-blog-posts";
import { useBlogTags } from "@/hooks/use-blog-tags";

type BlogPageClientProps = {
  page?: string;
  q?: string;
  tag?: string;
};

export function BlogPageClient({ page, q, tag }: BlogPageClientProps) {
  const {
    data: postsData,
    isLoading,
    isError,
  } = useBlogPosts({ page, q, tag });
  const { data: tagsData } = useBlogTags();

  const tags = tagsData?.tags ?? [];
  const currentQ = postsData?.q ?? (q ?? "").trim();
  const currentTag = postsData?.tag ?? (tag ?? "").trim();
  const parsedPage = Number.parseInt(page ?? "1", 10);
  const currentPage =
    postsData?.page ??
    (Number.isFinite(parsedPage) && parsedPage > 0 ? parsedPage : 1);

  return (
    <div className="bg-background text-foreground">
      <section
        data-header-scheme="dark"
        className="pt-32 pb-12 bg-foreground/5 dark:bg-card/30"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <header className="space-y-3">
            <h1 className="font-serif text-4xl md:text-5xl font-light tracking-tight">
              Blog
            </h1>
            <p className="max-w-3xl text-muted-foreground">
              Field notes, how-to guides, and design ideas for plaster and
              microcement projects.
            </p>
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              <span>
                Showing {postsData?.items.length ?? 0} of {postsData?.total ?? 0}{" "}
                post
                {(postsData?.total ?? 0) === 1 ? "" : "s"}
              </span>
              <span aria-hidden>•</span>
              <Link href="/blog/rss.xml" className="text-primary hover:underline">
                RSS
              </Link>
              <span aria-hidden>•</span>
              <span>Page size: {BLOG_PAGE_SIZE}</span>
            </div>
          </header>
        </div>
      </section>

      <section className="py-12">
        <div className="mx-auto max-w-7xl space-y-8 px-4 sm:px-6 lg:px-8">
          <div className="rounded-xl border bg-foreground/5 p-5">
            <BlogSearchForm q={currentQ} tag={currentTag} tags={tags} />
          </div>

          {isLoading ? (
            <div className="rounded-lg border bg-foreground/5 p-8 text-center">
              <p className="text-muted-foreground">Loading posts...</p>
            </div>
          ) : isError ? (
            <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-8 text-center">
              <p className="text-destructive">
                Unable to load blog posts right now.
              </p>
            </div>
          ) : !postsData || postsData.items.length === 0 ? (
            <div className="rounded-lg border bg-foreground/5 p-8 text-center">
              <p className="text-lg font-medium">No posts found</p>
              <p className="text-muted-foreground">
                Try a different search term or clear filters.
              </p>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2">
              {postsData.items.map((post) => (
                <BlogPostCard key={post.id} post={post} />
              ))}
            </div>
          )}

          <BlogPagination
            page={currentPage}
            totalPages={postsData?.totalPages ?? 1}
            q={currentQ}
            tag={currentTag}
          />
        </div>
      </section>
    </div>
  );
}
